import React, { useEffect, useMemo } from "react";
import type { HousingItem, SelectedVariant } from "../../types";
import { installmentLabel } from "../ViewCollection/utils";
import { pluralizeRu } from "../../../../utils/pluralizeRu";

type Props = {
    housing: HousingItem | null;
    selectedIds: Set<string>;
    disabledByLimit: boolean;
    onToggleVariant: (variant: SelectedVariant) => void;
    onClose: () => void;
};

const formatPrice = (value?: number | null) =>
    typeof value === "number" ? `${value.toLocaleString("ru-RU")} ₽` : "—";

export const VariantsDrawer: React.FC<Props> = ({
    housing,
    selectedIds,
    disabledByLimit,
    onToggleVariant,
    onClose,
}) => {
    const variants = useMemo(() => {
        if (!housing) return [];

        return housing.pricing?.flatMap((tier, tierIndex) =>
            tier.layouts.flatMap((layout, layoutIndex) =>
                layout.paymentPlans.map((plan, planIndex) => ({
                    id: `${housing.id}::${tierIndex}-${layoutIndex}-${planIndex}`,
                    tier,
                    layout,
                    plan,
                })),
            ),
        ) ?? [];
    }, [housing]);

    useEffect(() => {
        if (!housing) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [housing, onClose]);

    if (!housing) return null;

    return (
        <div className="ai-drawer-backdrop" onClick={onClose}>
            <aside
                className="ai-variants-drawer"
                aria-label={`Варианты в ${housing.housingName}`}
                aria-modal="true"
                role="dialog"
                onClick={(event) => event.stopPropagation()}
            >
                <header className="ai-variants-drawer__header">
                    <div>
                        <span className="ai-variants-drawer__eyebrow">Все варианты в ЖК</span>
                        <h2>{housing.housingName}</h2>
                        <p>{housing.address || "Адрес уточняется"}</p>
                    </div>
                    <button type="button" className="ai-variants-drawer__close" onClick={onClose} aria-label="Закрыть">
                        ×
                    </button>
                </header>

                <div className="ai-variants-drawer__summary">
                    <strong>{variants.length} {pluralizeRu(variants.length, ["вариант", "варианта", "вариантов"])}</strong>
                    <span>Выберите до трёх для подборки клиента</span>
                </div>

                <div className="ai-variants-drawer__list">
                    {variants.length ? variants.map(({ id, tier, layout, plan }) => {
                        const selected = selectedIds.has(id);
                        const disabled = disabledByLimit && !selected;

                        return (
                            <article className="ai-drawer-variant" key={id}>
                                <div className="ai-drawer-variant__title">
                                    <div>
                                        <strong>{layout.roomType} · {layout.area} м²</strong>
                                        <span>{tier.floorRange} этаж · {installmentLabel(plan.installmentPlan, plan.installmentPeriod)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`ai-drawer-variant__choose ${selected ? "is-selected" : ""}`}
                                        disabled={disabled}
                                        onClick={() => onToggleVariant({
                                            id,
                                            housingId: housing.id,
                                            roomType: layout.roomType,
                                            area: layout.area,
                                            fullPrice: plan.fullPrice,
                                            monthlyPrice: plan.monthlyPrice,
                                            firstPayment: plan.firstPayment,
                                            pricePerMeter: plan.pricePerMeter,
                                            installmentPlan: plan.installmentPlan,
                                            installmentPeriod: plan.installmentPeriod ?? null,
                                            floorRange: tier.floorRange ?? null,
                                            images: housing.images ?? [],
                                            housing,
                                        })}
                                    >
                                        {selected ? "В подборке" : "Выбрать"}
                                    </button>
                                </div>
                                <dl className="ai-drawer-variant__prices">
                                    <div><dt>Стоимость</dt><dd>{formatPrice(plan.fullPrice)}</dd></div>
                                    <div><dt>В месяц</dt><dd>{formatPrice(plan.monthlyPrice)}</dd></div>
                                    <div><dt>Первый взнос</dt><dd>{formatPrice(plan.firstPayment)}</dd></div>
                                </dl>
                            </article>
                        );
                    }) : (
                        <p className="ai-drawer-empty">Варианты для этого ЖК уточняются у менеджера.</p>
                    )}
                </div>
            </aside>
        </div>
    );
};
